import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { ReactNode } from "react";
import { trackEvent } from "../util/analytics";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import { Stack, FormHelperText } from "@mui/material";

type SliderWithInputProps<T extends FieldValues> = {
  name: string;
  label: ReactNode;
  control: Control<T>;
  hideLabel?: boolean;
  labelUnitSuffix?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  helperText?: string;
};

export const SliderWithInput = <T extends FieldValues>({
  name,
  label,
  control,
  hideLabel = false,
  labelUnitSuffix = "%",
  min = 0,
  max = 100,
  step = 5,
  disabled = false,
  helperText,
}: SliderWithInputProps<T>) => (
  <Box>
    <Grid container spacing={2} sx={{ alignItems: "center" }}>
      {!hideLabel && (
        <Grid size={3}>
          <Box id={name}>{label}</Box>
        </Grid>
      )}
      <Grid
        size={hideLabel ? 9 : 7}
        sx={{
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <Controller
          name={name as Path<T>}
          control={control}
          render={({ field }) => (
            <Slider
              aria-labelledby={name}
              max={max}
              min={min}
              step={step}
              disabled={disabled}
              {...field}
              onChange={(_, value) => {
                if (Array.isArray(value)) throw new Error("unexpected array");
                const numericValue: number = value;
                field.onChange(numericValue);
                trackEvent(
                  EventCategory.Baseline,
                  "Change Field Value",
                  name,
                  numericValue,
                );
              }}
            />
          )}
        />
      </Grid>
      <Grid size={hideLabel ? 3 : 2}>
        <Controller
          name={name as Path<T>}
          control={control}
          render={({ field }) => (
            <Stack direction="row">
              <Typography variant="body1" sx={{ minWidth: "32px" }}>
                {field.value}
              </Typography>
              <Typography variant="body1">{labelUnitSuffix}</Typography>
            </Stack>
          )}
        />
      </Grid>
    </Grid>
    {helperText && (
      <FormHelperText sx={{ ml: 2, mt: 0 }}>{helperText}</FormHelperText>
    )}
  </Box>
);
